import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Card, Form, Input, Button, Switch, Tag, App as AntdApp, Spin, Select, Row, Col,
} from 'antd';
import { ArrowLeft, MessageCircle, Plus, Building2 } from 'lucide-react';
import { PageHeader } from '@/components/common/PageHeader';
import { adminFeedbackService } from '@/services/feedbackService';
import { orgService } from '@/services/rolesService';

/**
 * V1.0.37: Trang tạo/sửa chủ đề lắng nghe (image 1 của design).
 * V1.0.38: Thêm chọn nhiều khoa liên quan (khoa đầu = khoa chính, dùng khi tạo task auto).
 */
export function FeedbackTopicFormPage() {
  const { message } = AntdApp.useApp();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const isEdit = !!id;

  const [form] = Form.useForm();
  const [emoji, setEmoji] = useState<string>('💬');
  const [isActive, setIsActive] = useState(true);
  const [targetGroups, setTargetGroups] = useState<string[]>(['Nội trú', 'Khám bệnh']);
  const [newTag, setNewTag] = useState('');

  // V1.0.38: Khoa liên quan — multi select, thứ tự = ưu tiên
  const [relatedDepartmentIds, setRelatedDepartmentIds] = useState<string[]>([]);

  // Preview state
  const [previewName, setPreviewName] = useState('');
  const [previewMessage, setPreviewMessage] = useState('');

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['admin-feedback-topic', id],
    queryFn: () => adminFeedbackService.getTopic(id!),
    enabled: isEdit,
  });

  // V1.0.38: Load org tree để chọn khoa liên quan
  const { data: orgTree = [] } = useQuery({
    queryKey: ['org-tree'],
    queryFn: () => orgService.getTree(),
  });
  const flattenOrg = (nodes: any[], prefix = ''): Array<{label: string; value: string}> => {
    const out: Array<{label: string; value: string}> = [];
    for (const n of nodes) {
      // V1.0.47: Bỏ khoa inactive khỏi list chọn
      if (n.isActive === false) {
        if (n.children?.length) out.push(...flattenOrg(n.children, prefix));
        continue;
      }
      out.push({ label: `${prefix}${n.name}`, value: n.id });
      if (n.children?.length) out.push(...flattenOrg(n.children, prefix + '— '));
    }
    return out;
  };
  const deptOptions = flattenOrg(orgTree);

  useEffect(() => {
    if (detail) {
      form.setFieldsValue({
        name: detail.name,
        description: detail.description,
        listeningMessage: detail.listeningMessage,
      });
      setEmoji(detail.emoji || '💬');
      setIsActive(detail.isActive);
      setTargetGroups(detail.targetGroups || []);
      // V1.0.38: Pre-fill khoa liên quan theo sortOrder ASC
      setRelatedDepartmentIds(
        (detail.relatedDepartments ?? [])
          .slice()
          .sort((a, b) => a.sortOrder - b.sortOrder)
          .map(d => d.departmentId)
      );
      setPreviewName(detail.name);
      setPreviewMessage(detail.listeningMessage || '');
    }
  }, [detail, form]);

  const saveMut = useMutation({
    mutationFn: async (values: any) => {
      const payload = {
        name: values.name.trim(),
        emoji: emoji,
        description: values.description?.trim() || undefined,
        listeningMessage: values.listeningMessage?.trim() || undefined,
        isActive,
        targetGroups,
        relatedDepartmentIds, // V1.0.38
        sortOrder: detail?.sortOrder ?? 0,
      };
      if (isEdit) {
        return adminFeedbackService.updateTopic(id!, payload);
      }
      return adminFeedbackService.createTopic(payload);
    },
    onSuccess: () => {
      message.success(isEdit ? 'Đã cập nhật chủ đề' : 'Đã tạo chủ đề mới');
      qc.invalidateQueries({ queryKey: ['admin-feedback-topics'] });
      navigate('/admin/feedback/topics');
    },
    onError: (e: any) => {
      message.error(e?.response?.data?.message ?? 'Lưu thất bại');
    },
  });

  const handleSubmit = () => {
    form.validateFields().then(values => saveMut.mutate(values));
  };

  const addTag = () => {
    const tag = newTag.trim();
    if (!tag) return;
    if (!targetGroups.includes(tag)) {
      setTargetGroups([...targetGroups, tag]);
    }
    setNewTag('');
  };

  // Emoji picker đơn giản — 1 set emoji thông dụng
  const commonEmojis = ['💬', '🏥', '🩺', '⚕️', '📋', '🍽️', '💰', '🚑', '😊', '🌟', '❤️', '💡', '📞', '🚪'];

  if (isEdit && detailLoading) {
    return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
  }

  return (
    <>
      <PageHeader
        breadcrumb={
          <span style={{ cursor: 'pointer' }} onClick={() => navigate('/admin/feedback/topics')}>
            QUẢN TRỊ &gt; HỘP THƯ LẮNG NGHE
          </span>
        }
        title={isEdit ? 'Sửa chủ đề lắng nghe' : 'Tạo chủ đề lắng nghe mới'}
        subtitle="Cùng lắng nghe và thấu hiểu để mang lại dịch vụ tốt nhất cho bệnh nhân."
        extra={
          <Button
            icon={<ArrowLeft size={14} />}
            onClick={() => navigate('/admin/feedback/topics')}
          >
            Quay lại danh sách
          </Button>
        }
      />

      <Row gutter={[20, 20]}>
        {/* Form */}
        <Col xs={24} lg={15} xl={16}>
          <Card>
          <Form form={form} layout="vertical" onValuesChange={(_, all) => {
            setPreviewName(all.name || '');
            setPreviewMessage(all.listeningMessage || '');
          }}>
            <Row gutter={16}>
              <Col xs={24} sm={16}>
                <Form.Item
                  name="name"
                  label="Tên chủ đề chia sẻ"
                  rules={[{ required: true, message: 'Vui lòng nhập tên chủ đề' }]}
                >
                  <Input placeholder="Vd: Thái độ phục vụ" size="large" />
                </Form.Item>
              </Col>

              <Col xs={24} sm={8}>
                <Form.Item label="Biểu tượng cảm xúc">
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    border: '1px solid #d9d9d9', borderRadius: 8,
                    padding: '0 12px', height: 40,
                  }}>
                    <span style={{ fontSize: 24 }}>{emoji}</span>
                    <select
                      value={emoji}
                      onChange={(e) => setEmoji(e.target.value)}
                      style={{
                        border: 'none', outline: 'none',
                        flex: 1, fontSize: 14, cursor: 'pointer', background: 'transparent',
                      }}
                    >
                      {commonEmojis.map(e => (
                        <option key={e} value={e}>{e} Chọn icon</option>
                      ))}
                    </select>
                  </div>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="listeningMessage" label="Thông điệp lắng nghe">
              <Input.TextArea
                rows={3}
                placeholder="Chúng tôi mong muốn lắng nghe điều gì về chủ đề này?"
              />
            </Form.Item>

            <Form.Item name="description" label="Mô tả chi tiết (tùy chọn)">
              <Input.TextArea
                rows={2}
                placeholder="Giải thích thêm về chủ đề này..."
              />
            </Form.Item>

            {/* V1.0.38: Chọn khoa liên quan — khoa đầu tiên = khoa chính */}
            <Form.Item
              label={
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                  <Building2 size={14} />
                  Khoa/Phòng liên quan
                </span>
              }
              extra={
                <span style={{ fontSize: 11 }}>
                  Chọn 1 hoặc nhiều khoa. <b>Khoa đầu tiên</b> sẽ là khoa chính —
                  khi tạo task xử lý feedback, task sẽ được giao về khoa đầu tiên
                  và mặc định gán cho trưởng khoa đó.
                </span>
              }
            >
              <Select
                mode="multiple"
                placeholder="Chọn khoa/phòng liên quan đến chủ đề..."
                value={relatedDepartmentIds}
                onChange={setRelatedDepartmentIds}
                options={deptOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                filterOption={(input, opt) =>
                  (opt?.label as string ?? '').toLowerCase().includes(input.toLowerCase())
                }
                maxTagCount="responsive"
              />
              {relatedDepartmentIds.length > 0 && (
                <div style={{ marginTop: 6, fontSize: 11, color: '#1e6feb' }}>
                  ⭐ Khoa chính:{' '}
                  <strong>
                    {deptOptions.find(d => d.value === relatedDepartmentIds[0])?.label ?? '—'}
                  </strong>
                </div>
              )}
            </Form.Item>

            <Form.Item label="Nhóm đối tượng áp dụng (legacy)">
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
                {targetGroups.map(tag => (
                  <Tag
                    key={tag}
                    closable
                    onClose={() => setTargetGroups(targetGroups.filter(t => t !== tag))}
                    style={{ background: '#e8f1ff', color: '#1e6feb', border: 'none', padding: '4px 10px' }}
                  >
                    {tag}
                  </Tag>
                ))}
                <div style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <Input
                    size="small"
                    placeholder="+ Thêm mới"
                    value={newTag}
                    onChange={(e) => setNewTag(e.target.value)}
                    onPressEnter={addTag}
                    style={{ width: 120 }}
                  />
                  <Button size="small" icon={<Plus size={12} />} onClick={addTag} />
                </div>
              </div>
            </Form.Item>
          </Form>
        </Card>
        </Col>

        {/* Right panel: status + preview */}
        <Col xs={24} lg={9} xl={8}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Card>
              <div style={{ fontWeight: 600, marginBottom: 14 }}>Trạng thái ghi nhận</div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div>
                  <div style={{ fontWeight: 500 }}>Đang lắng nghe</div>
                  <div style={{ fontSize: 11, color: '#7d8a98' }}>Cho phép người bệnh gửi phản hồi</div>
                </div>
                <Switch checked={isActive} onChange={setIsActive} />
              </div>
            </Card>

            {/* Preview */}
            <Card style={{ background: '#f8faff' }}>
              <div style={{
                background: '#1e6feb', color: '#fff', padding: '6px 12px',
              borderRadius: 6, fontSize: 11, fontWeight: 600,
              marginBottom: 12, display: 'inline-block',
              textTransform: 'uppercase',
            }}>
              Xem trước giao diện
            </div>
            <div style={{
              background: '#e8f1ff', borderRadius: 8, padding: 12,
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{ fontSize: 32 }}>{emoji}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, color: '#1e293b' }}>
                  {previewName || 'Tên chủ đề sẽ ở đây'}
                </div>
                <div style={{ fontSize: 11, color: '#5b6875', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {previewMessage || 'Thông điệp lắng nghe sẽ hiển thị tại đây...'}
                </div>
              </div>
            </div>
            </Card>
          </div>
        </Col>
      </Row>

      {/* Bottom actions */}
      <div style={{
        marginTop: 20, display: 'flex', justifyContent: 'flex-end', gap: 10,
      }}>
        <Button size="large" onClick={() => navigate('/admin/feedback/topics')}>
          Hủy thay đổi
        </Button>
        <Button
          type="primary" size="large"
          loading={saveMut.isPending}
          onClick={handleSubmit}
          style={{ background: '#1e6feb' }}
        >
          {isEdit ? 'Lưu thay đổi' : 'Tạo chủ đề'}
        </Button>
      </div>
    </>
  );
}

export default FeedbackTopicFormPage;
